import type { AgentChatMessage, ManagedPr } from "./types";
import {
  isAutofixMessage,
  isAutofixSentinelMessage,
} from "./groupAutofixMessages";
import { isMonitorWakeMessage } from "./monitorWake";
import { stripMarkdownForPreview } from "./PrCard";
import { parseLinearPromptContext } from "./LinearMessagePreview";

/**
 * One row of the right-sidebar conversation mini-map: a user message the
 * person actually sent, plus the pull-request outcome attributed to that
 * message's turn.
 */
export interface ConversationMiniMapEntry {
  /** Message id — drives the `#msg-<id>` anchor and scroll-to navigation. */
  id: string;
  /**
   * Row label: the Linear ticket title for messages kicked off from a Linear
   * issue, otherwise the markdown-stripped message text.
   */
  preview: string;
  /** The message is a Linear ticket prompt; render a Linear glyph + title. */
  isLinear: boolean;
  timestamp: Date;
  /** Managed PRs first opened during this message's turn (newest last). */
  createdPrs: ManagedPr[];
  /**
   * A PR already existed when this message was sent and this message did not
   * itself open one, i.e. the follow-up prompt pushed updates onto open work.
   */
  updatedExistingPr: boolean;
}

/** A user turn the person actually sent: not an autofix trigger/sentinel or a monitor wake-up. */
function isRealUserMessage(m: AgentChatMessage): boolean {
  return (
    m.role === "user" &&
    !isAutofixMessage(m) &&
    !isAutofixSentinelMessage(m) &&
    !isMonitorWakeMessage(m)
  );
}

export function hasRealUserMessages(chatMessages: AgentChatMessage[]): boolean {
  return chatMessages.some(isRealUserMessage);
}

/**
 * Row label for a user message. Linear ticket prompts (the `<issue …>` XML
 * the backend attaches) collapse to just the ticket title; everything else
 * falls back to the markdown-stripped text, then to an attachment label.
 */
function describeMessage(message: AgentChatMessage): {
  preview: string;
  isLinear: boolean;
} {
  const linear = parseLinearPromptContext(message.content ?? "");
  if (linear) return { preview: linear.title, isLinear: true };

  const stripped = stripMarkdownForPreview(message.content ?? "");
  if (stripped) return { preview: stripped, isLinear: false };

  const attachments = message.attachments ?? [];
  if (attachments.length > 0) {
    return {
      preview: attachments.some((att) => att.type === "image")
        ? "Sent an image"
        : "Sent an attachment",
      isLinear: false,
    };
  }
  return { preview: "Message", isLinear: false };
}

/**
 * Build the conversation mini-map for a session: the ordered list of real
 * user messages (autofix triggers and sentinels excluded so the map mirrors
 * the transcript) annotated with the PR outcome of each turn.
 *
 * PR attribution is timestamp-based since managed PRs carry no message
 * pointer: each PR is credited to the last user message sent at or before its
 * `created_at` (the turn that was active when it opened), or to the first
 * message when the PR predates the conversation. A later message with no PR
 * of its own is marked `updatedExistingPr` when a PR already existed when it
 * was sent.
 */
export function buildConversationMiniMap(
  chatMessages: AgentChatMessage[],
  managedPrs: ManagedPr[] | undefined,
): ConversationMiniMapEntry[] {
  const userMessages = chatMessages.filter(isRealUserMessage);
  if (userMessages.length === 0) return [];

  const messageTimes = userMessages.map((m) => m.timestamp.getTime());
  const prTimes = (managedPrs ?? [])
    .map((pr) => ({ pr, t: Date.parse(pr.created_at) }))
    .filter(({ t }) => Number.isFinite(t));

  const earliestPrTime = prTimes.length
    ? Math.min(...prTimes.map(({ t }) => t))
    : Infinity;

  // Credit each PR to the user message whose turn was active when it opened:
  // the latest message sent at or before the PR's creation time, or the first
  // message when the PR predates every message.
  const createdByIndex = new Map<number, ManagedPr[]>();
  for (const { pr, t } of prTimes) {
    let index = -1;
    let bestTime = -Infinity;
    for (let j = 0; j < messageTimes.length; j++) {
      if (messageTimes[j] <= t && messageTimes[j] >= bestTime) {
        bestTime = messageTimes[j];
        index = j;
      }
    }
    if (index === -1) index = 0;
    const bucket = createdByIndex.get(index);
    if (bucket) bucket.push(pr);
    else createdByIndex.set(index, [pr]);
  }

  return userMessages.map((message, i) => {
    const createdPrs = createdByIndex.get(i) ?? [];
    const { preview, isLinear } = describeMessage(message);
    return {
      id: message.id,
      preview,
      isLinear,
      timestamp: message.timestamp,
      createdPrs,
      updatedExistingPr:
        createdPrs.length === 0 && earliestPrTime < messageTimes[i],
    };
  });
}
